import { useEffect, useState } from "react";
import { Lock, RefreshCw, FileDown, LogOut, FileText, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/lib/enrollment/api";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

const PWD_KEY = "prsvr.admin.pwd";

export default function AdminDashboard() {
  const [pwd, setPwd] = useState(() => {
    try {
      return sessionStorage.getItem(PWD_KEY) || "";
    } catch {
      return "";
    }
  });
  const [authed, setAuthed] = useState(false);
  const [input, setInput] = useState("");
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [authing, setAuthing] = useState(false);

  // Validate stored pwd on mount
  useEffect(() => {
    if (pwd) verify(pwd, /*silent*/ true);
  }, []);

  // Once authed, load submissions
  useEffect(() => {
    if (authed) refresh();
  }, [authed]);

  async function verify(password, silent = false) {
    setAuthing(true);
    try {
      const res = await fetch(`${API}/admin/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password }),
      });
      if (!res.ok) {
        if (!silent) toast.error("Invalid password");
        sessionStorage.removeItem(PWD_KEY);
        setPwd("");
        setAuthed(false);
        return false;
      }
      sessionStorage.setItem(PWD_KEY, password);
      setPwd(password);
      setAuthed(true);
      return true;
    } catch (e) {
      if (!silent) toast.error("Login failed");
      return false;
    } finally {
      setAuthing(false);
    }
  }

  async function refresh() {
    if (!pwd) return;
    setLoading(true);
    try {
      const res = await fetch(`${API}/admin/submissions`, {
        headers: { "X-Admin-Password": pwd },
      });
      if (!res.ok) throw new Error("Failed to load");
      const body = await res.json();
      setSubmissions(body.submissions || []);
    } catch (e) {
      toast.error("Could not load submissions");
    } finally {
      setLoading(false);
    }
  }

  function logout() {
    sessionStorage.removeItem(PWD_KEY);
    setPwd("");
    setAuthed(false);
    setSubmissions([]);
  }

  function downloadPdf(email) {
    const url = `${API}/admin/submissions/${encodeURIComponent(email)}/pdf?admin_password=${encodeURIComponent(pwd)}`;
    window.open(url, "_blank", "noopener,noreferrer");
  }

  if (!authed) {
    return (
      <div className="min-h-screen bg-cream-grain flex items-center justify-center px-5" data-testid="admin-login">
        <div className="bg-white border border-[#E5E0D8] rounded-sm p-8 sm:p-10 max-w-md w-full">
          <div className="flex items-center gap-3 mb-2">
            <div className="inline-flex w-10 h-10 items-center justify-center rounded-sm bg-[#1B3B2B] text-white">
              <Lock size={18} />
            </div>
            <div className="label-eyebrow text-[#CD7F32]">Admin · PRSVR</div>
          </div>
          <h1 className="font-serif-display text-3xl text-[#1B3B2B] leading-tight">
            Admissions Review
          </h1>
          <p className="text-sm text-[#4B5563] mt-2">
            Sign in to view and download submitted enrollment agreements.
          </p>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (input.trim()) verify(input.trim());
            }}
            className="mt-6 space-y-4"
          >
            <label className="block">
              <span className="label-eyebrow text-[#1A1A1A]">Admin Password</span>
              <input
                type="password"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                autoFocus
                data-testid="admin-password-input"
                className="mt-2 w-full bg-white border-2 border-[#E5E0D8] rounded-sm px-4 py-3 focus:outline-none focus:border-[#1B3B2B] transition-colors"
                placeholder="••••••••"
              />
            </label>
            <button
              type="submit"
              disabled={authing || !input.trim()}
              data-testid="admin-login-btn"
              className="w-full inline-flex items-center justify-center gap-2 bg-[#1B3B2B] text-white hover:bg-[#132A13] disabled:opacity-60 transition-colors rounded-sm px-6 py-3 font-semibold uppercase tracking-wider text-sm"
            >
              {authing ? "Verifying…" : "Sign In"}
            </button>
          </form>
        </div>
      </div>
    );
  }

  const submitted = submissions.filter((s) => s.status === "submitted");
  const drafts = submissions.filter((s) => s.status !== "submitted");

  return (
    <div className="min-h-screen bg-cream-grain" data-testid="admin-dashboard">
      {/* Top bar */}
      <header className="bg-[#1B3B2B] text-white">
        <div className="max-w-[1300px] mx-auto px-6 py-5 flex items-center justify-between">
          <div>
            <div className="label-eyebrow text-[#CD7F32]">Scholar Athlete Academy</div>
            <div className="font-serif-display text-2xl leading-none mt-1">Admissions Review</div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={refresh}
              data-testid="admin-refresh-btn"
              className="inline-flex items-center gap-2 border border-white/30 hover:bg-white/10 transition-colors rounded-sm px-4 py-2 text-xs uppercase tracking-widest font-semibold"
            >
              <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Refresh
            </button>
            <button
              onClick={logout}
              data-testid="admin-logout-btn"
              className="inline-flex items-center gap-2 bg-[#CD7F32] hover:bg-[#B06A26] transition-colors rounded-sm px-4 py-2 text-xs uppercase tracking-widest font-semibold"
            >
              <LogOut size={14} /> Sign Out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-[1300px] mx-auto px-6 py-10">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
          <Stat label="Submitted" value={submitted.length} />
          <Stat label="Drafts" value={drafts.length} />
          <Stat
            label="Paid Fees"
            value={submissions.filter((s) => s.payment_status === "paid").length}
          />
          <Stat label="Total" value={submissions.length} />
        </div>

        <Section title="Submitted Enrollments" subtitle="Signed agreements ready for review.">
          <Table rows={submitted} onDownload={downloadPdf} pwd={pwd} />
        </Section>

        <Section title="In-Progress Drafts" subtitle="Families who haven't finished yet.">
          <Table rows={drafts} onDownload={downloadPdf} pwd={pwd} />
        </Section>
      </main>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="bg-white border border-[#E5E0D8] rounded-sm p-5">
      <div className="label-eyebrow">{label}</div>
      <div className="font-serif-display text-4xl text-[#1B3B2B] mt-1">{value}</div>
    </div>
  );
}

function Section({ title, subtitle, children }) {
  return (
    <section className="mb-12">
      <div className="mb-4">
        <h2 className="font-serif-display text-2xl text-[#1B3B2B]">{title}</h2>
        {subtitle && <p className="text-sm text-[#4B5563] mt-1">{subtitle}</p>}
      </div>
      {children}
    </section>
  );
}

function Table({ rows, onDownload, pwd }) {
  if (!rows.length) {
    return (
      <div className="bg-white border border-[#E5E0D8] rounded-sm p-8 text-center text-sm text-[#4B5563]">
        No records.
      </div>
    );
  }
  return (
    <div className="bg-white border border-[#E5E0D8] rounded-sm overflow-x-auto" data-testid="admin-table">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-[#E5E0D8] text-left">
            <Th>Student</Th>
            <Th>Parent</Th>
            <Th>Tier</Th>
            <Th>Rate</Th>
            <Th>Docs</Th>
            <Th>Fee</Th>
            <Th>Submitted</Th>
            <Th align="right">Actions</Th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.enrollment_id} className="border-b border-[#E5E0D8]/60 hover:bg-[#F4F1EA]/40">
              <Td>
                <div className="font-semibold text-[#1A1A1A]">{r.student_name || "—"}</div>
                <div className="text-xs text-[#4B5563]">{r.email}</div>
              </Td>
              <Td>{r.parent_name || "—"}</Td>
              <Td>
                <div>{r.tier_label || "—"}</div>
                <div className="text-xs text-[#4B5563]">
                  {r.tier_path ? r.tier_path.replace("_", " ") : ""}
                </div>
              </Td>
              <Td>{r.tier_rate_display || "—"}</Td>
              <Td>
                <DocsPopover row={r} pwd={pwd} />
              </Td>
              <Td>
                {r.payment_status === "paid" ? (
                  <span className="inline-flex items-center px-2 py-0.5 bg-[#1B3B2B] text-white text-[10px] uppercase tracking-widest rounded-sm">
                    Paid
                  </span>
                ) : (
                  <span className="inline-flex items-center px-2 py-0.5 bg-[#F4F1EA] text-[#4B5563] text-[10px] uppercase tracking-widest rounded-sm border border-[#E5E0D8]">
                    Unpaid
                  </span>
                )}
              </Td>
              <Td>
                <span className="text-xs tabular-nums">
                  {r.submitted_at ? new Date(r.submitted_at).toLocaleString() : "—"}
                </span>
              </Td>
              <Td align="right">
                <button
                  onClick={() => onDownload(r.email)}
                  data-testid={`admin-download-${r.email}`}
                  className="inline-flex items-center gap-2 border border-[#1B3B2B] text-[#1B3B2B] hover:bg-[#1B3B2B] hover:text-white transition-colors rounded-sm px-3 py-1.5 text-xs uppercase tracking-widest font-semibold"
                >
                  <FileDown size={12} /> PDF
                </button>
              </Td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Th({ children, align = "left" }) {
  return (
    <th
      className="label-eyebrow px-4 py-3 text-[#4B5563]"
      style={{ textAlign: align }}
    >
      {children}
    </th>
  );
}

function Td({ children, align = "left" }) {
  return (
    <td className="px-4 py-3 align-top" style={{ textAlign: align }}>
      {children}
    </td>
  );
}

function humanSize(bytes) {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function DocsPopover({ row, pwd }) {
  const [open, setOpen] = useState(false);
  const [docs, setDocs] = useState(null);
  const [loading, setLoading] = useState(false);
  const count = row.doc_count || 0;

  const loadDocs = async () => {
    if (docs) return;
    setLoading(true);
    try {
      const res = await fetch(
        `${API}/admin/enrollments/${encodeURIComponent(row.email)}/documents`,
        { headers: { "X-Admin-Password": pwd } },
      );
      if (!res.ok) throw new Error("Failed to load");
      const body = await res.json();
      setDocs(body.documents || []);
    } catch (e) {
      toast.error("Could not load documents");
      setDocs([]);
    } finally {
      setLoading(false);
    }
  };

  const DOC_LABELS = {
    birth_certificate: "Birth Certificate",
    immunization: "Immunization Record",
    report_card: "Most Recent Report Card",
  };

  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (o) loadDocs(); }}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={`docs-popover-${row.email}`}
          className={[
            "inline-flex items-center gap-1 px-2 py-0.5 text-[10px] uppercase tracking-widest rounded-sm cursor-pointer transition-colors",
            count >= 3
              ? "bg-[#1B3B2B] text-white hover:bg-[#132A13]"
              : count > 0
                ? "bg-[#CD7F32]/20 text-[#1B3B2B] border border-[#CD7F32]/40 hover:bg-[#CD7F32]/30"
                : "bg-[#F4F1EA] text-[#4B5563] border border-[#E5E0D8] hover:bg-[#E5E0D8]/40",
          ].join(" ")}
          title="View uploaded documents"
        >
          <FileText size={11} />
          {count}/3
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-96 p-5 bg-white border-[#E5E0D8]" align="end">
        <div className="text-[10px] tracking-[0.18em] uppercase font-semibold text-[#CD7F32]">
          Uploaded Documents
        </div>
        <div className="font-serif-display text-xl text-[#1B3B2B] mt-1 leading-tight">
          {row.student_name || row.email}
        </div>
        <div className="text-xs text-[#4B5563] mt-0.5">{row.email}</div>

        <div className="mt-4 space-y-2">
          {loading && (
            <div className="flex items-center gap-2 text-sm text-[#4B5563] py-3">
              <Loader2 className="animate-spin" size={14} /> Loading…
            </div>
          )}
          {!loading && docs && docs.length === 0 && (
            <div className="text-sm text-[#4B5563] py-3">
              No documents uploaded yet for this family.
            </div>
          )}
          {!loading && docs && docs.length > 0 && (
            <>
              {["birth_certificate", "immunization", "report_card"].map((dt) => {
                const d = docs.find((x) => x.doc_type === dt);
                if (!d) {
                  return (
                    <div
                      key={dt}
                      className="flex items-center justify-between gap-3 p-3 border border-dashed border-[#E5E0D8] rounded-sm bg-[#F4F1EA]/40"
                    >
                      <div className="min-w-0">
                        <div className="text-[10px] tracking-[0.18em] uppercase font-semibold text-[#4B5563]">
                          {DOC_LABELS[dt]}
                        </div>
                        <div className="text-xs text-[#991B1B] mt-0.5">Not uploaded</div>
                      </div>
                    </div>
                  );
                }
                return (
                  <div
                    key={d.id}
                    className="flex items-center justify-between gap-3 p-3 border border-[#E5E0D8] rounded-sm bg-white"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="text-[10px] tracking-[0.18em] uppercase font-semibold text-[#1A1A1A]">
                        {DOC_LABELS[dt]}
                      </div>
                      <div className="text-sm truncate text-[#1A1A1A] mt-0.5" title={d.original_filename}>
                        {d.original_filename}
                      </div>
                      <div className="text-xs text-[#4B5563]">
                        {humanSize(d.size)} · {new Date(d.created_at).toLocaleDateString()}
                      </div>
                    </div>
                    <a
                      href={`${API}/admin/documents/${d.id}?admin_password=${encodeURIComponent(pwd)}`}
                      target="_blank"
                      rel="noreferrer"
                      data-testid={`download-doc-${d.id}`}
                      className="flex-shrink-0 inline-flex items-center gap-1 border border-[#1B3B2B] text-[#1B3B2B] hover:bg-[#1B3B2B] hover:text-white transition-colors rounded-sm px-3 py-1.5 text-[10px] uppercase tracking-widest font-semibold"
                    >
                      <FileDown size={11} /> View
                    </a>
                  </div>
                );
              })}
            </>
          )}
        </div>

        <div className="mt-4 pt-3 border-t border-[#E5E0D8] text-[10px] text-[#4B5563]">
          Clicking <strong>View</strong> opens the document in a new tab. Files are streamed
          directly from secure storage — no public links are generated.
        </div>
      </PopoverContent>
    </Popover>
  );
}
